import { createSelector } from 'reselect';
import { List } from 'immutable';
import { memoizeImmutableResult, chainSelectors } from 'app/utils/redux';
import { ENTITY_PREFIX } from 'app/remote/constants';

/**
 * Takes one or more arrays of the form `[entityType, fieldName, fieldName, ...]`, and returns a
 * memoized selector function that takes a list of entities, and returns a list with entities of the
 * given types flattened into the leaf entities referenced by the given association field names.
 *
 * TODO: This is essentially a small domain-specific language for creating selectors that flatten
 * entities. A more generically useful (but harder to implement) abstraction would be a DSL for
 * creating selectors that denormalize entities while keeping the same nested structure (which would
 * then be easy to flatten to a List).
 *
 * @param {Array} ...paths - one or more `{ type, path: [...] }` objects
 * @returns {function} a memoized selector function of the form:
 * `(state, entity|List([entity, ...])) => List([association entity, ...])`
 * that flattens the given entity types to their leaves, and doesn't modify entities of other types.
 */
const makeFlattenLeaves = (...paths) => {
  const pairsPerPath = paths.map(({ type, path }) => {
    let currentType = type;
    const result = [];

    for (const associationName of path) {
      result.push([currentType, associationName]);

      currentType = currentType.associations[associationName].type;
    }

    return result;
  });

  const pairSets = [];
  const maxPairs = Math.max(...pairsPerPath.map(pairs => pairs.length));

  for (let i = 0; i < maxPairs; i++) {
    pairSets.push(
      pairsPerPath
      .map(pairs => pairs[i])
      .filter(x => x),
    );
  }

  return chainSelectors(
    ...pairSets.map((pairSet, index) => shallowFlatten(pairSet, index > 0)),
  );
};

/**
 * Takes one or more pairs of `[entityType, fieldName]` and returns a memoized selector
 * function that takes a list of entities and returns a list with the given entity types flattened
 * into the entities referenced at the given association field name.
 */
const shallowFlatten = (typeFieldsPairs, allowNonMatchingEntities = false) => {
  for (const [entityType, associationName] of typeFieldsPairs) {
    if (!entityType.associations[associationName]) {
      throw new Error(
        `Could not find association '${associationName}' on type ${entityType.typeName}`,
      );
    }
  }

  const flatten = createSelector(
    [
      (state, input) => input,
      ...typeFieldsPairs.map(([entityType, associationName]) => {
        const associationStorePrefix = entityType.associations[associationName].type.storePrefix;

        return (state) => state.getIn([ENTITY_PREFIX, associationStorePrefix]);
      }),
    ],
    (input, ...associationTables) => {
      if (!input) {
        const typeNames = typeFieldsPairs.map(([type]) => type.typeName);
        throw new Error(
          `makeFlattenLeaves expected ${typeNames.join('/')} entity or list of entities, ` +
          `but got '${input}'`,
        );
      }

      const entityList = input instanceof List ? input : List([input]);

      if (entityList.size === 0) {
        return List();
      } else {
        return entityList
          .flatMap(entity => {
            const hasMatchingPairs = typeFieldsPairs
              .some(([entityType]) => entity instanceof entityType);

            if (!hasMatchingPairs) {
              if (allowNonMatchingEntities) {
                // Just return the entity as is if it doesn't match one of the given types.
                return List([entity]);
              } else {
                throw new Error(
                  `makeFlattenLeaves got unexpected entity type '${entity.schema.typeName}'`,
                );
              }
            } else {
              return List(typeFieldsPairs).flatMap(([entityType, associationName], index) => {
                if (!(entity instanceof entityType)) {
                  return List();
                }

                // Get the entity/entities referenced by each matching field.
                const associationValue = entity[associationName];

                if (!associationValue) {
                  return List();
                }

                const isCollection = entityType.associations[associationName].collection;
                const associationIds = isCollection ? associationValue : List([associationValue]);

                return associationIds.map(id => associationTables[index].get(id));
              });
            }
          })
          .filter(x => x);
      }
    },
  );

  // Only update when associations that are actually referenced change.
  return memoizeImmutableResult(flatten);
};

export default makeFlattenLeaves;
